import React, { useEffect, useRef, useState } from 'react'

export const TRANSITION_EXIT_MS = 150
export const TRANSITION_TOTAL_MS = 490

function prefersReducedMotion() {
  return typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

export default function GuidedScreenTransition({ screenKey, children }) {
  const [scenes, setScenes] = useState(() => [{ key: screenKey, node: children, phase: 'active' }])
  const sequenceRef = useRef(0)
  const timersRef = useRef([])
  const previousKeyRef = useRef(screenKey)

  useEffect(() => {
    if (screenKey === previousKeyRef.current) {
      setScenes(current => current.map(scene =>
        scene.key === screenKey ? { ...scene, node: children } : scene
      ))
      return undefined
    }

    const exitingKey = previousKeyRef.current
    previousKeyRef.current = screenKey
    sequenceRef.current += 1
    const sequence = sequenceRef.current
    timersRef.current.forEach(clearTimeout)
    timersRef.current = []

    if (prefersReducedMotion()) {
      setScenes([{ key: screenKey, node: children, phase: 'active' }])
      return undefined
    }

    setScenes(current => {
      const settled = current.find(scene => scene.key === exitingKey) ||
        current.find(scene => scene.phase !== 'exiting')
      return [
        settled ? { ...settled, phase: 'exiting' } : null,
        { key: screenKey, node: children, phase: 'entering' },
      ].filter(Boolean)
    })

    timersRef.current.push(setTimeout(() => {
      if (sequenceRef.current !== sequence) return
      setScenes(current => current.filter(scene => scene.phase !== 'exiting'))
    }, TRANSITION_EXIT_MS))

    timersRef.current.push(setTimeout(() => {
      if (sequenceRef.current !== sequence) return
      setScenes(current => current.map(scene => ({ ...scene, phase: 'active' })))
    }, TRANSITION_TOTAL_MS))

    return undefined
  }, [screenKey, children])

  useEffect(() => () => timersRef.current.forEach(clearTimeout), [])

  return (
    <div className="guided-transition">
      {scenes.map(scene => (
        <div
          key={scene.key}
          className={`guided-transition__scene guided-transition__scene--${scene.phase}`}
          data-testid="guided-transition-scene"
          data-phase={scene.phase}
        >
          {scene.node}
        </div>
      ))}
    </div>
  )
}
